import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const FillOverridesDialog = ({ open, onClose, overrides, order, lineItem }) => {
  const orderId = order?.orderId;
  const rxNumber = lineItem?.rxNumber;
  const itemName = lineItem?.itemName;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: 2,
          pb: 1,
          px: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display='flex' alignItems='center' gap={1.25}>
          <WarningAmberIcon fontSize='large' sx={{ color: (t) => t.palette.warning.main }} />
          <Typography
            variant='h6'
            sx={{
              textTransform: 'none',
              letterSpacing: 0.2,
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            Fill Overrides
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        <Box mt={1.5} mb={2}>
          <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>
            Order / Line Item Details
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, 1fr)',
              gap: 2,
              p: 2,
              bgcolor: (t) => t.palette.action.hover,
              borderRadius: 1,
            }}
          >
            <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
              <Typography variant='caption' color='text.secondary'>
                Order ID
              </Typography>
              <Typography variant='body2'>{orderId || 'N/A'}</Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
              <Typography variant='caption' color='text.secondary'>
                RX Number
              </Typography>
              <Typography variant='body2'>{rxNumber || 'N/A'}</Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
              <Typography variant='caption' color='text.secondary'>
                Item
              </Typography>
              <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                {itemName || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: (t) => t.palette.action.hover }}>
                  Field Edited
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: (t) => t.palette.action.hover }}>
                  Previous Value
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: (t) => t.palette.action.hover }}>
                  Updated Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(overrides && overrides.length ? overrides : lineItem?.fillOverrides || []).map(
                ({ key, originalValue, overriddenValue }) => (
                  <TableRow
                    key={`${key}-${originalValue}-${overriddenValue}`}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: (t) => t.palette.action.hover },
                      '& td': { py: 1.25 },
                    }}
                  >
                    <TableCell
                      sx={{ textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.4 }}
                    >
                      {String(key).replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {String(originalValue)}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'primary.main' }}
                    >
                      {String(overriddenValue)}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant='contained'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FillOverridesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  overrides: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      originalValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
      overriddenValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    })
  ),
  order: PropTypes.shape({
    orderId: PropTypes.string,
  }),
  lineItem: PropTypes.shape({
    rxNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    itemName: PropTypes.string,
    processingState: PropTypes.string,
    fillOverrides: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        originalValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
        overriddenValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
      })
    ),
  }),
};

FillOverridesDialog.defaultProps = {
  overrides: [],
  order: null,
  lineItem: null,
};

export default FillOverridesDialog;
